/**
 * CenterStage API Service
 * Proxies all CenterStage operations through the backend to keep credentials server-side.
 */

import { fetchWithAuthRetry, filenameFromContentDisposition } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface CenterstageCaption {
  language: string;
  languageCode: string;
  kind: 'captions' | 'subtitles';
  file: string;
}

export interface CenterstageVideo {
  id: string;
  title: string;
  url: string;
  description?: string;
  thumbnailUrl?: string;
  posterUrl?: string;
  duration?: number;
  captions?: CenterstageCaption[];
  /** Detail only: the video's library concepts. */
  concepts?: CenterstageVideoConcept[];
  /** Detail only, once Centerstage exposes it. */
  type?: LibraryAttributeOption;
  /** Detail only, once Centerstage exposes it. */
  setting?: LibraryAttributeOption;
}

export interface CenterstageVideoConcept {
  id: number;
  name: string;
  category: string;
}

/** A video type or setting: `value` is what the filter sends, `label` what the page shows. */
export interface LibraryAttributeOption {
  value: string;
  label: string;
}

export interface LibraryCategory {
  id: number;
  name: string;
}

export interface LibraryConcept {
  id: number;
  name: string;
  categoryId: number | null;
}

/** What the Centerstage page filters by. Types and settings are empty until Centerstage exposes them. */
export interface LibraryFilters {
  categories: LibraryCategory[];
  concepts: LibraryConcept[];
  types: LibraryAttributeOption[];
  settings: LibraryAttributeOption[];
}

/** The library page's active filters; each one set narrows the list (AND). */
export interface LibraryQuery {
  q?: string;
  categoryId?: number;
  conceptId?: number;
  type?: string;
  setting?: string;
}

export interface CenterstageSearchResult {
  videos: CenterstageVideo[];
  total: number;
}

export interface CenterstageTranscriptDownload {
  blob: Blob;
  filename: string;
}

function transcriptFilename(response: Response, videoId: string, locale: string): string {
  return filenameFromContentDisposition(response.headers.get('content-disposition'))
    ?? `transcript-${videoId}_${locale}.pdf`;
}

/**
 * Search videos in CenterStage. `publicOnly` limits the results to public
 * videos; by default non-public videos are included.
 */
export async function searchVideos(query: string, limit: number = 15, options: { publicOnly?: boolean } = {}): Promise<CenterstageSearchResult> {
  if (!query.trim()) {
    return { videos: [], total: 0 };
  }

  const params = new URLSearchParams({ query, limit: String(limit), ...(options.publicOnly ? { publicOnly: 'true' } : {}) });
  const response = await fetch(`${API_BASE_URL}/centerstage/videos/search?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Search failed' }));
    throw new Error(error.message || `CenterStage API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * One page of the public videos tagged with a CenterStage concept, newest
 * first. Rows carry no captions; use getVideo for those.
 */
export async function listConceptVideos(conceptId: number, page: number = 1, limit: number = 25): Promise<CenterstageSearchResult> {
  const params = new URLSearchParams({ conceptId: String(conceptId), page: String(page), limit: String(limit) });
  const response = await fetch(`${API_BASE_URL}/centerstage/videos?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to list videos' }));
    throw new Error(error.message || `CenterStage API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single video by ID from CenterStage (includes captions)
 */
export async function getVideo(videoId: string): Promise<CenterstageVideo | null> {
  const response = await fetch(`${API_BASE_URL}/centerstage/videos/${videoId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json().catch(() => ({ message: 'Failed to get video' }));
    throw new Error(error.message || `CenterStage API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

async function readLibraryResponse<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: fallback }));
    throw new Error(error.message || `CenterStage API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/** The categories, concepts, types and settings the Centerstage page filters by. */
export async function fetchLibraryFilters(): Promise<LibraryFilters> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/centerstage/library/filters`, { method: 'GET' }, { json: 'never' });
  return readLibraryResponse(response, 'Failed to load library filters');
}

/** Public video counts per library concept id. A concept whose count failed is absent. */
export async function fetchConceptCounts(): Promise<Record<number, number>> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/centerstage/library/concept-counts`, { method: 'GET' }, { json: 'never' });
  const body = await readLibraryResponse<{ counts: Record<number, number> }>(response, 'Failed to load concept counts');
  return body.counts;
}

/** One page of the public library matching every filter given. */
export async function listLibrary(query: LibraryQuery, page: number = 1, limit: number = 24): Promise<CenterstageSearchResult> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.categoryId !== undefined) params.set('categoryId', String(query.categoryId));
  if (query.conceptId !== undefined) params.set('conceptId', String(query.conceptId));
  if (query.type) params.set('type', query.type);
  if (query.setting) params.set('setting', query.setting);
  params.set('page', String(page));
  params.set('limit', String(limit));
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/centerstage/library?${params}`, { method: 'GET' }, { json: 'never' });
  return readLibraryResponse(response, 'Failed to load the library');
}

export async function checkTranscriptAvailability(
  videoId: string,
  locale: string,
  signal: AbortSignal,
): Promise<boolean> {
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/centerstage/transcripts/${encodeURIComponent(videoId)}/${encodeURIComponent(locale)}/exists`,
    { method: 'GET', signal },
    { json: 'never' },
  );

  if (!response.ok) {
    throw new Error(`Transcript availability request failed (${response.status})`);
  }

  const body = await response.json() as { exists?: unknown };
  return body.exists === true;
}

export async function downloadTranscript(
  videoId: string,
  locale: string,
): Promise<CenterstageTranscriptDownload> {
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/centerstage/transcripts/${encodeURIComponent(videoId)}/${encodeURIComponent(locale)}/download`,
    { method: 'POST' },
    { json: 'never' },
  );

  if (!response.ok) {
    throw new Error(`Transcript download request failed (${response.status})`);
  }

  return {
    blob: await response.blob(),
    filename: transcriptFilename(response, videoId, locale),
  };
}
